'use client';

import favoriteIconActive from '@/assets/images/icons/icon-favorite-active.png';
import favoriteIcon from '@/assets/images/icons/icon-favorite.png';
import FaIcon from '@/components/icon/FaIcon/FaIcon';
import SignInPromptModal from '@/components/layouts/SignInPromptModal/SignInPromptModal';
import ImageDetailModal from '@/components/ui/ImageDetailModal/ImageDetailModal';
import ModalBase from '@/components/ui/ModalBase/ModalBase';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import ProgressAnime from '@/components/ui/ProgressAnime/ProgressAnime';
import RoundIcon from '@/components/ui/RoundIcon/RoundIcon';
import RoundTextIcon from '@/components/ui/RoundTextIcon/RoundTextIcon';
import { type MaterialCategoryType, SALE_STATUS, UNAVAILABLE_SALE_STATUS_LABEL } from '@/constants';
import { swal } from '@/lib/sweetalert';
import { useGlobalLoadingAtom } from '@/state';
import { BadgeStatus } from '@/types';
import { formatCurrency } from '@/utils/formatters';
import { pickErrorMessages } from '@/utils/functions';
import { sendSelectItemEvent, sendViewItemListEvent } from '@/utils/ga';
import { getImageLayoutSizes } from '@/utils/nextImage';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import type { Route } from 'next';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { deleteFavoriteMaterial } from '../../api/deleteFavoriteMaterial';
import { postFavoriteMaterial } from '../../api/postFavoriteMaterial';
import type { GfMaterialDetail } from '../../types';
import { groupProductsByCategory } from '../../utils/groupProductsByCategory';
import styles from './MaterialDetailModal.module.scss';

function MaterialDetailModal({
  isMaterialModalOpen,
  setIsMaterialModalOpen,
  selectedMaterial,
  workId,
  toggleFavoriteState,
  backgroundColor,
}: {
  isMaterialModalOpen: boolean;
  setIsMaterialModalOpen: (isMaterialModalOpen: boolean) => void;
  selectedMaterial: GfMaterialDetail | null;
  workId: number;
  toggleFavoriteState: (id: number, type: MaterialCategoryType) => void; //APIのロードなしで親の状態変数を変えたい
  backgroundColor?: string;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [signInPromptModalOpen, setSignInPromptModalOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const session = useSession();
  const router = useRouter();
  const [_, startGlobalLoading] = useGlobalLoadingAtom();

  // モーダルが開かれた時にview_item_listイベントを発火
  useEffect(() => {
    if (!isMaterialModalOpen || !selectedMaterial) return;

    const items = selectedMaterial.availableProducts.map((product) => ({
      item_id: product.id.toString(),
      item_name: product.name,
      item_brand: selectedMaterial?.work.name || '',
      material_ids: selectedMaterial.id.toString(),
    }));

    sendViewItemListEvent(items, 'コマ詳細モーダル - 利用可能なアイテム');
  }, [isMaterialModalOpen, selectedMaterial]);

  //お気に入りクリック
  const handleSaveMaterial = async (
    selectedMaterialIsFavorite: boolean,
    id: number,
    materialType: MaterialCategoryType,
  ) => {
    try {
      setIsLoading(true);
      if (!session.data) {
        setSignInPromptModalOpen(true);
        return;
      }
      if (selectedMaterialIsFavorite) {
        await deleteFavoriteMaterial({ gfMaterialId: id });
      } else {
        await postFavoriteMaterial({ gfMaterialId: id });
      }
      toggleFavoriteState(id, materialType);
      router.refresh();
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductClick = (productId: number, materialId: number) => {
    // 遷移先URLを構築
    const url = `/product/${productId}?materialId=${materialId}&workId=${workId}`;
    startGlobalLoading();

    // GAイベント送信（router.pushより前に発火し、遷移による取りこぼしを防止）
    const product = selectedMaterial?.availableProducts.find((p) => p.id === productId);
    if (product) {
      const ecommerce_items = [
        {
          item_id: productId.toString(),
          item_name: product.name,
          item_brand: selectedMaterial?.work.name || '',
          material_ids: selectedMaterial?.id ? selectedMaterial.id.toString() : '',
        },
      ];
      sendSelectItemEvent(ecommerce_items);
    }

    router.push(url as Route);
  };

  const handleImageClick = () => {
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
  };

  return (
    <ModalBase
      isOpen={isMaterialModalOpen}
      onClose={() => setIsMaterialModalOpen(false)}
      maskBgColor={backgroundColor}
      modalContainerSize='large'
      bgColor='gray100'
      showDefaultCloseButton={true}
      closeButtonBgColor={backgroundColor}
      showDefaultCloseButtonOnlyOnPc={true}
      showBottomCloseButton={true}
      closeOnBackgroundClick={true}
    >
      {!selectedMaterial ? (
        <ProgressAnime />
      ) : (
        <div className={styles.materialDetailModal}>
          <div className={styles.materialDetailContent}>
            <div className={styles.titleWrapper}>
              <div className={styles.materialDetailTitle}>作りたいアイテムを選ぼう</div>
              <div className={styles.materialDetailSubtext}>※アイテムによって使用できないコマがあります。</div>
            </div>
            <div className={styles.materialContent}>
              <div className={styles.imageWrapper} onClick={handleImageClick}>
                <NextImageView
                  src={selectedMaterial.imagePath}
                  alt={selectedMaterial.name}
                  fill
                  sizes={getImageLayoutSizes({ sp: '100vw', pc: '400px' })}
                  objectFit='contain'
                  priority
                />
                {selectedMaterial.badgeStatus === BadgeStatus.new.value && (
                  <div className={styles.badgeStatus}>
                    <RoundTextIcon
                      bgColor='orange'
                      text={BadgeStatus[selectedMaterial.badgeStatus].text}
                      size='defaultFixed'
                    />
                  </div>
                )}
                <button
                  className={styles.materialSaveButton}
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation(); // 画像拡大を防ぐためイベントバブリングを停止
                    handleSaveMaterial(selectedMaterial.isFavorite, selectedMaterial.id, selectedMaterial.categoryName);
                  }}
                  disabled={isLoading}
                >
                  <RoundIcon
                    imgSrc={selectedMaterial.isFavorite ? favoriteIconActive.src : favoriteIcon.src}
                    size='defaultFixed'
                    bgColor='white'
                  />
                </button>
              </div>
            </div>
            <p className={styles.materialName}>{selectedMaterial.name}</p>
            <div className={styles.productContent}>
              {selectedMaterial.availableProducts.length > 0 && (
                <div className={styles.categoryGroups}>
                  {groupProductsByCategory(selectedMaterial.availableProducts).map((group) => (
                    <div key={group.categoryName} className={styles.categoryGroup}>
                      <h4 className={styles.categoryGroupTitle}>{group.categoryName}</h4>
                      <hr className={styles.categoryGroupDivider} />
                      <div className={styles.categoryGroupProducts}>
                        {group.products.map((product) => {
                          // ラベル導出と narrowing を兼ねるため、判定は saleStatus の直接比較で行う
                          const unavailableLabel =
                            product.saleStatus === SALE_STATUS.onSale
                              ? null
                              : UNAVAILABLE_SALE_STATUS_LABEL[product.saleStatus];
                          const isOnSale = unavailableLabel === null;
                          return (
                            <button
                              className={styles.productRowButton}
                              type='button'
                              onClick={() => handleProductClick(product.id, selectedMaterial.id)}
                              key={product.id}
                              disabled={!isOnSale}
                              data-testid='product-selection-button'
                            >
                              <div
                                className={`${styles.productRow} ${isOnSale ? styles.productRowActive : styles.productRowDisabled}`}
                              >
                                <div className={styles.productRowContentWrapper}>
                                  <NextImageView
                                    src={product.thumbnailPaths.sub}
                                    alt={product.name}
                                    fill={false}
                                    width={40}
                                    height={40}
                                    objectFit='contain'
                                    className={styles.productImage}
                                  />
                                  <div className={styles.productMessageWrapper}>
                                    <p className={styles.productMessage}>{product.name}</p>
                                    {isOnSale ? (
                                      <p className={styles.productPrice}>
                                        <span className={styles.productPriceCurrency}>¥</span>
                                        {formatCurrency(product.basePrice)}〜＋送料
                                      </p>
                                    ) : (
                                      <p className={styles.productUnavailableLabel}>{unavailableLabel}</p>
                                    )}
                                  </div>
                                </div>
                                {isOnSale && (
                                  <div className={styles.productRowArrowContainer}>
                                    <FaIcon icon={faChevronRight} />
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <SignInPromptModal
        isOpen={signInPromptModalOpen}
        onClose={() => setSignInPromptModalOpen(false)}
        description='コマをお気に入りに登録するには、ログインが必要です。'
      />
      {selectedMaterial && (
        <ImageDetailModal
          isOpen={isImageModalOpen}
          onClose={handleCloseImageModal}
          imageUrl={selectedMaterial.imagePath}
        />
      )}
    </ModalBase>
  );
}

export default MaterialDetailModal;
